import { useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Box,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  Rating,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import useSearch from "../../hooks/useSearch";
import FilterPanel from "../../common-components/filters/FilterPanel";
import useCatalogFilters from "../../hooks/useCatalogFilters";
import { frontendRoutes } from "../../utility/frontendRoutes";
import {
  productSortOptions,
  resolveImageUri,
  shopSortOptions,
} from "../../utility/constants";
import { useDebounce } from "../../hooks/useDebounce";
import ImageWithLoader from "../../common-components/ImageWithLoader";
import VerifiedBadge from "../../common-components/VerifiedBadge";
import { trackEvent } from "../../utility/analytics";
import {
  buildProductPath,
  buildShopPath,
  buildUserPath,
  formatProductPriceLabel,
  isPriceHidden,
  isProductNegotiable,
} from "../../utility/utilityFunctions";

const TABS = [
  { value: "products", label: "Products" },
  { value: "shops", label: "Shops" },
  { value: "people", label: "People" },
];

// ── Result rows/cards ─────────────────────────────────────────────

const ProductResultCard = ({ product }) => {
  const imageUri = product.images?.[0]
    ? resolveImageUri(product.images[0])
    : null;
  const shop = product.shop;

  return (
    <Box
      component={Link}
      to={buildProductPath(product)}
      sx={{
        display: "block",
        textDecoration: "none",
        color: "inherit",
        border: "1px solid #e5e7eb",
        borderRadius: 2,
        overflow: "hidden",
        height: "100%",
        "&:hover": {
          borderColor: "#046287",
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        },
        transition: "border-color 0.15s, box-shadow 0.15s",
      }}
    >
      <Box
        sx={{
          width: "100%",
          aspectRatio: "1 / 1",
          bgcolor: "#f5f5f5",
          overflow: "hidden",
        }}
      >
        {imageUri ? (
          <ImageWithLoader src={imageUri} alt={product.productName} />
        ) : (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography color="text.secondary" fontSize={12}>
              No image
            </Typography>
          </Box>
        )}
      </Box>
      <Box sx={{ p: 1.5 }}>
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            mb: 0.25,
          }}
        >
          {product.productName}
        </Typography>
        <Typography
          variant="body2"
          fontWeight={700}
          color="#046287"
          sx={{ mb: 0.5 }}
        >
          {formatProductPriceLabel(product)}
        </Typography>
        {isProductNegotiable(product) && !isPriceHidden(product) && (
          <Typography
            variant="caption"
            fontWeight={600}
            color="success.dark"
            display="block"
            sx={{ mb: 0.5 }}
          >
            Open to offers
          </Typography>
        )}
        {product.numReviews > 0 && (
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}
          >
            <Rating
              value={product.rating || 0}
              precision={0.1}
              readOnly
              size="small"
            />
            <Typography variant="body2" color="text.secondary" fontSize={11}>
              ({product.numReviews})
            </Typography>
          </Box>
        )}
        {shop?.shopName && (
          <Typography
            variant="body2"
            color="text.secondary"
            fontSize={11}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            <Box
              component="span"
              sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
            >
              {shop.shopName}
            </Box>
            {shop.isIdentityVerified && <VerifiedBadge size={12} />}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const ResultRow = ({
  to,
  avatarUri,
  fallbackInitial,
  title,
  verified,
  subtitle,
}) => (
  <Box
    component={Link}
    to={to}
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      px: 2,
      py: 1.5,
      borderBottom: "1px solid #f3f4f6",
      textDecoration: "none",
      color: "inherit",
      "&:hover": { bgcolor: "#fafafa" },
      "&:last-child": { borderBottom: "none" },
    }}
  >
    <Avatar src={avatarUri} sx={{ width: 48, height: 48, flexShrink: 0 }}>
      {fallbackInitial}
    </Avatar>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        variant="body2"
        fontWeight={600}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        <Box
          component="span"
          sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
        >
          {title}
        </Box>
        {verified && <VerifiedBadge size={14} />}
      </Typography>
      {subtitle && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {subtitle}
        </Typography>
      )}
    </Box>
  </Box>
);

const ShopResultRow = ({ shop }) => {
  const avatarUri = shop.profilePhoto?.path
    ? resolveImageUri(shop.profilePhoto)
    : null;

  return (
    <ResultRow
      to={buildShopPath(shop)}
      avatarUri={avatarUri}
      fallbackInitial={(shop.shopName || "S")[0]?.toUpperCase()}
      title={shop.shopName}
      verified={shop.isIdentityVerified}
      subtitle={shop.description}
    />
  );
};

const UserResultRow = ({ user }) => {
  const avatarUri = user.profilePhoto?.path
    ? resolveImageUri(user.profilePhoto)
    : null;

  return (
    <ResultRow
      to={buildUserPath(user)}
      avatarUri={avatarUri}
      fallbackInitial={(user.name || "U")[0]?.toUpperCase()}
      title={user.name}
      verified={user.isIdentityVerified}
      subtitle={user.bio}
    />
  );
};

const EmptyState = ({ hasSearch, isLoading }) => {
  if (isLoading) return null;
  return (
    <Box sx={{ textAlign: "center", py: 8 }}>
      <Typography color="text.secondary">
        {hasSearch ? "No results found" : "Type or apply a filter to search"}
      </Typography>
    </Box>
  );
};

// ── Page ──────────────────────────────────────────────────────────

const SearchPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialQuery = searchParams.get("q") || "";
  const initialCategory = searchParams.get("category");
  // Plural params (comma-separated) take precedence over the singular
  // `category` param — mirrors the mobile app's initialCategories/
  // initialCategory precedence when arriving pre-filtered from a product/
  // shop detail page.
  const initialCategories = (searchParams.get("categories") || "")
    .split(",")
    .filter(Boolean);
  const initialDeliveryMethods = (searchParams.get("deliveryMethods") || "")
    .split(",")
    .filter(Boolean);
  const initialPaymentMethods = (searchParams.get("paymentMethods") || "")
    .split(",")
    .filter(Boolean);

  const [inputValue, setInputValue] = useState(initialQuery);
  const debouncedQuery = useDebounce(inputValue.trim(), 400);

  const filters = useCatalogFilters({
    initialCategories: initialCategories.length
      ? initialCategories
      : initialCategory
        ? [initialCategory]
        : [],
    initialDeliveryMethods,
    initialPaymentMethods,
  });
  const {
    selectedCategories,
    selectedDeliveryMethods,
    selectedPaymentMethods,
    selectedPaymentTimings,
    locationCountry,
    locationState,
    hasActiveFilters,
  } = filters;
  const [activeTab, setActiveTab] = useState("products");
  const [productSort, setProductSort] = useState("top_rated");
  const [shopSort, setShopSort] = useState("top_rated");

  const sentinelRef = useRef(null);

  const { productsQuery, shopsQuery, peopleQuery } = useSearch({
    q: debouncedQuery,
    categories: selectedCategories,
    deliveryMethods: selectedDeliveryMethods,
    paymentMethods: selectedPaymentMethods,
    paymentTimings: selectedPaymentTimings,
    productSort,
    shopSort,
    state: locationState,
    country: locationCountry,
  });

  const products = useMemo(
    () => productsQuery.data?.pages.flatMap((p) => p.products.results) ?? [],
    [productsQuery.data],
  );
  const shops = useMemo(
    () => shopsQuery.data?.pages.flatMap((p) => p.shops.results) ?? [],
    [shopsQuery.data],
  );
  const users = useMemo(
    () => peopleQuery.data?.pages.flatMap((p) => p.users.results) ?? [],
    [peopleQuery.data],
  );

  const counts = {
    products: productsQuery.data?.pages?.[0]?.products?.total ?? 0,
    shops: shopsQuery.data?.pages?.[0]?.shops?.total ?? 0,
    people: peopleQuery.data?.pages?.[0]?.users?.total ?? 0,
  };

  const hasSearch = !!debouncedQuery || hasActiveFilters;

  const activeQuery =
    activeTab === "products"
      ? productsQuery
      : activeTab === "shops"
        ? shopsQuery
        : peopleQuery;
  const activeResults =
    activeTab === "products" ? products : activeTab === "shops" ? shops : users;
  const showLoader = activeQuery.isLoading && hasSearch;

  useEffect(() => {
    if (debouncedQuery) {
      trackEvent("search_performed", {
        query: debouncedQuery,
        categories: selectedCategories,
        source: "web",
      });
    }
  }, [debouncedQuery, selectedCategories]);

  // Once all three first pages have loaded, switch to the first tab that has results
  useEffect(() => {
    if (!hasSearch) return;
    const allLoaded =
      !productsQuery.isLoading &&
      !shopsQuery.isLoading &&
      !peopleQuery.isLoading;
    if (!allLoaded || counts[activeTab] > 0) return;
    const firstWithResults = TABS.find((t) => counts[t.value] > 0);
    if (firstWithResults) setActiveTab(firstWithResults.value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productsQuery.isLoading, shopsQuery.isLoading, peopleQuery.isLoading]);

  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          activeQuery.hasNextPage &&
          !activeQuery.isFetchingNextPage
        ) {
          activeQuery.fetchNextPage();
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, activeQuery.hasNextPage, activeQuery.isFetchingNextPage]);

  return (
    <Box sx={{ maxWidth: 1100, mx: "auto", px: { xs: 2, md: 4 }, py: 3 }}>
      {/* Search bar */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <IconButton
          onClick={() => navigate(frontendRoutes.HOME)}
          sx={{ color: "#046287" }}
          aria-label="Back to home feed"
        >
          <ArrowBackIcon />
        </IconButton>
        <TextField
          fullWidth
          autoFocus
          placeholder="What are you looking for?"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#046287" }} />
              </InputAdornment>
            ),
            endAdornment: inputValue && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setInputValue("")}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 3,
              bgcolor: "white",
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#046287",
              },
            },
          }}
        />
      </Box>

      {/* Tabs with result counts */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{ minHeight: 40, mb: 1, borderBottom: "1px solid #e5e7eb" }}
        TabIndicatorProps={{ sx: { bgcolor: "#046287" } }}
      >
        {TABS.map((t) => (
          <Tab
            key={t.value}
            value={t.value}
            label={hasSearch ? `${t.label} (${counts[t.value]})` : t.label}
            sx={{
              minHeight: 40,
              textTransform: "none",
              fontWeight: 600,
              "&.Mui-selected": { color: "#046287" },
            }}
          />
        ))}
      </Tabs>

      <FilterPanel
        filters={filters}
        onClearAll={filters.clearAll}
        sortOptions={
          activeTab === "products"
            ? productSortOptions
            : activeTab === "shops"
              ? shopSortOptions
              : undefined
        }
        sortValue={activeTab === "products" ? productSort : shopSort}
        onSortChange={activeTab === "products" ? setProductSort : setShopSort}
        // Open on a pre-selected delivery/payment chip so it's visible.
        defaultOpen={
          initialDeliveryMethods.length > 0 || initialPaymentMethods.length > 0
        }
      />

      <Box sx={{ borderTop: "1px solid #e5e7eb", pt: 2 }}>
        {showLoader ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress sx={{ color: "#046287" }} />
          </Box>
        ) : activeResults.length === 0 ? (
          <EmptyState hasSearch={hasSearch} isLoading={activeQuery.isLoading} />
        ) : activeTab === "products" ? (
          <>
            <Grid container spacing={2}>
              {products.map((product) => (
                <Grid item xs={6} sm={4} md={3} key={product._id || product.id}>
                  <ProductResultCard product={product} />
                </Grid>
              ))}
            </Grid>
            <Box
              ref={sentinelRef}
              sx={{ py: 4, display: "flex", justifyContent: "center" }}
            >
              {productsQuery.isFetchingNextPage && (
                <CircularProgress size={28} sx={{ color: "#046287" }} />
              )}
            </Box>
          </>
        ) : activeTab === "shops" ? (
          <>
            <Box sx={{ border: "1px solid #f3f4f6", borderRadius: 2 }}>
              {shops.map((shop) => (
                <ShopResultRow key={shop._id || shop.id} shop={shop} />
              ))}
            </Box>
            <Box
              ref={sentinelRef}
              sx={{ py: 4, display: "flex", justifyContent: "center" }}
            >
              {shopsQuery.isFetchingNextPage && (
                <CircularProgress size={28} sx={{ color: "#046287" }} />
              )}
            </Box>
          </>
        ) : (
          <>
            <Box sx={{ border: "1px solid #f3f4f6", borderRadius: 2 }}>
              {users.map((user) => (
                <UserResultRow key={user._id || user.id} user={user} />
              ))}
            </Box>
            <Box
              ref={sentinelRef}
              sx={{ py: 4, display: "flex", justifyContent: "center" }}
            >
              {peopleQuery.isFetchingNextPage && (
                <CircularProgress size={28} sx={{ color: "#046287" }} />
              )}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default SearchPage;
